import LegalLayout from "@/components/LegalLayout";

const H = ({ children }) => (
  <h2 className="font-display text-2xl tracking-tight text-[#171512]">{children}</h2>
);

const A = ({ children }) => (
  <a href={`mailto:${children}`} className="text-[#B9822E] underline-offset-4 hover:underline">
    {children}
  </a>
);

export default function Terms() {
  return (
    <LegalLayout title="Terms and Conditions" updated="Last updated: 17 September 2026">
      <section data-testid="terms-content" className="space-y-10">
        <div className="space-y-4">
          <H>1. About these terms</H>
          <p>
            These terms apply to nostos.in and to the Nostos waitlist and Founding Community. The
            website is run by Nostos, based in Sarjapur, Bengaluru, Karnataka, India
            (&quot;Nostos&quot;, &quot;we&quot;, &quot;us&quot;).
          </p>
          <p>
            By using the website or signing up, you agree to these terms and our Privacy Policy. If
            you don&apos;t agree, please don&apos;t sign up.
          </p>
          <p>
            These terms cover the pre-launch stage only. Buying Nostos products will be covered by
            separate terms of sale, which we&apos;ll publish before sales open.
          </p>
        </div>

        <div className="space-y-4">
          <H>2. Who can sign up</H>
          <ul className="list-disc space-y-2 pl-5">
            <li>You must be 18 or older.</li>
            <li>You can sign up once, using your own name, email address and phone number.</li>
            <li>You can&apos;t sign up for someone else or use extra email addresses or numbers to get more entries.</li>
            <li>
              The details you give us must be true. If they change, email us at{" "}
              <A>nostosmylkco@gmail.com</A> so you don&apos;t miss updates.
            </li>
          </ul>
        </div>

        <div className="space-y-4">
          <H>3. The waitlist</H>
          <ul className="list-disc space-y-2 pl-5">
            <li>Anyone who meets section 2 can join the waitlist, and it&apos;s free.</li>
            <li>
              Joining means you&apos;ll get updates about Nostos and the NIDRA launch, and may get
              the chance to buy from the limited first drop early.
            </li>
            <li>
              Joining isn&apos;t an order, pre-order or reservation. You don&apos;t have to buy
              anything, and we don&apos;t take any payment.
            </li>
          </ul>
        </div>

        <div className="space-y-4">
          <H>4. Early access</H>
          <p>
            Early access is a chance to buy before or alongside the general public, on terms
            we&apos;ll share when it opens. It isn&apos;t a guarantee.
          </p>
          <ul className="list-disc space-y-2 pl-5">
            <li>The first NIDRA drop will be limited in quantity.</li>
            <li>It may sell out before everyone on the list gets to buy.</li>
            <li>We can&apos;t promise any particular product, flavour, pack size or quantity will be available to you.</li>
            <li>We&apos;ll explain how early access works, including timing and order, when we announce it.</li>
          </ul>
        </div>

        <div className="space-y-4">
          <H>5. The Founding Community</H>
          <p>
            <strong>Joining.</strong> When you sign up, you can also choose to join the Founding
            Community by ticking the box on the form. It&apos;s optional and free.
          </p>
          <p>
            <strong>200 places.</strong> Only the first 200 people who choose to join become
            Founding Community members. Once all 200 places are taken, no more founding numbers
            are given out, and new sign-ups join the waitlist only.
          </p>
          <p>
            <strong>Founding numbers.</strong> Each member gets a unique founding number, shown as,
            for example, &quot;#020 of 200&quot;. Numbers are given automatically, in the order
            our system receives Founding Community sign-ups. There&apos;s no lucky draw or random
            selection. Your number depends only on when you joined.
          </p>
          <p>
            <strong>Your number stays yours.</strong> It doesn&apos;t change, and it can&apos;t be
            sold, transferred or given to anyone else. If you leave or your entry is removed, the
            number isn&apos;t given to anyone else.
          </p>
          <p>
            <strong>Joining later.</strong> If you&apos;re already on the waitlist, you can join the
            Founding Community later while places remain. Your number is then based on when you
            chose to join.
          </p>
          <p>
            <strong>Live count.</strong> Any count of places shown on the website is the real number
            of places taken.
          </p>
        </div>

        <div className="space-y-4">
          <H>6. Founding Community benefits</H>
          <p>Founding Community members may get:</p>
          <ul className="list-disc space-y-2 pl-5">
            <li>a unique founding number,</li>
            <li>chances to give feedback and have a say in what Nostos builds,</li>
            <li>early access to future product drops,</li>
            <li>founding-member discounts, and</li>
            <li>invitations to Nostos community events.</li>
          </ul>
          <p>
            These are community benefits and brand privileges. We&apos;ll share the details, timing
            and conditions of each one when we announce it. Some benefits may be limited, for
            example events with limited space or held in certain cities.
          </p>
          <p>
            Discounts apply only on the terms stated when they&apos;re offered and can&apos;t be
            exchanged for cash.
          </p>
          <p>
            We take your input seriously, but final decisions about Nostos and its products stay
            with Nostos.
          </p>
          <p>We may change or stop benefits. If a change affects members, we&apos;ll tell them.</p>
        </div>

        <div className="space-y-4">
          <H>7. What membership doesn&apos;t mean</H>
          <p>Being on the waitlist or in the Founding Community doesn&apos;t:</p>
          <ul className="list-disc space-y-2 pl-5">
            <li>make you an employee, contractor, partner, agent or representative of Nostos,</li>
            <li>give you ownership, shares or equity in Nostos,</li>
            <li>count as an investment or any kind of financial product, or</li>
            <li>entitle you to any payment, profit share or financial return.</li>
          </ul>
          <p>
            You won&apos;t be paid for giving feedback or attending events unless we agree otherwise
            in writing. You can&apos;t act or speak on behalf of Nostos.
          </p>
        </div>

        <div className="space-y-4">
          <H>8. Launch dates, prices and availability</H>
          <p>We haven&apos;t set a launch date. Any date we share is an estimate and may change.</p>
          <p>
            Product details, flavours, pack sizes, prices and availability may also change before
            launch. Prices will be confirmed at the time of sale.
          </p>
          <p>We&apos;ll only sell products once we have the licences and approvals the law requires.</p>
        </div>

        <div className="space-y-4">
          <H>9. Health information</H>
          <p>
            What we share on our website, in our messages and in our community is general wellness
            information. It isn&apos;t medical advice and doesn&apos;t replace advice from a doctor.
          </p>
          <p>
            Nostos products are not medicines and aren&apos;t meant to diagnose, treat, cure or
            prevent any disease. If you&apos;re pregnant, breastfeeding, taking medication, have a
            health condition or aren&apos;t sure a product is right for you, talk to your doctor
            first. Always read the label.
          </p>
        </div>

        <div className="space-y-4">
          <H>10. Our content</H>
          <p>
            The Nostos and NIDRA names, logos, designs and content on this website belong to Nostos.
            You can view and share links to the website for personal use, but you can&apos;t copy or
            use our content or brand for anything else without our written permission.
          </p>
        </div>

        <div className="space-y-4">
          <H>11. Your feedback</H>
          <p>
            When you share feedback, ideas, votes or suggestions with us, you allow Nostos to use
            them freely to build and improve our products and services, without paying you or
            crediting you. We won&apos;t publicly name you as the source without your permission.
          </p>
          <p>
            We don&apos;t have to use your feedback, keep it confidential or reply to it, and we may
            already be working on something similar.
          </p>
          <p>
            Please don&apos;t share anything confidential, anything that belongs to someone else, or
            details about your health unless we&apos;ve specifically asked.
          </p>
        </div>

        <div className="space-y-4">
          <H>12. Using the website and community</H>
          <p>Please don&apos;t:</p>
          <ul className="list-disc space-y-2 pl-5">
            <li>use the website for anything illegal,</li>
            <li>create fake, duplicate or automated sign-ups, or use bots to get founding numbers,</li>
            <li>try to change the order in which founding numbers are given,</li>
            <li>pretend to be someone else or give false details,</li>
            <li>try to access our systems or other people&apos;s data without permission,</li>
            <li>interfere with the website&apos;s security or operation,</li>
            <li>copy or scrape data from the website, or</li>
            <li>harass, abuse or threaten anyone in any Nostos community space or event.</li>
          </ul>
          <p>
            Some community spaces and events may have their own rules, which we&apos;ll share before
            you take part.
          </p>
        </div>

        <div className="space-y-4">
          <H>13. Removal from the list</H>
          <p>
            We may remove your entry, founding number or access to community spaces and events if
            you break these terms, your sign-up is fake, duplicated or automated, or the law
            requires it. Where reasonable, we&apos;ll tell you why first.
          </p>
          <p>If you&apos;re removed, you lose your place, your founding number and any linked benefits.</p>
          <p>
            You can leave at any time by emailing <A>nostosmylkco@gmail.com</A>.
          </p>
        </div>

        <div className="space-y-4">
          <H>14. Other services</H>
          <p>
            Our website and messages may link to or use services run by other companies, like social
            media or messaging apps. Their own terms apply, and we&apos;re not responsible for them.
          </p>
        </div>

        <div className="space-y-4">
          <H>15. Our responsibility</H>
          <p>
            We work to keep the website accurate and available, but we can&apos;t promise it will
            always be error-free or uninterrupted.
          </p>
          <p>
            Nothing in these terms limits your rights as a consumer under Indian law, including the
            Consumer Protection Act, 2019. Otherwise, to the extent the law allows, we&apos;re not
            responsible for indirect losses from using the website, the waitlist or the Founding
            Community.
          </p>
        </div>

        <div className="space-y-4">
          <H>16. Changes</H>
          <p>
            We may change, pause or end the waitlist, the Founding Community or any benefit. If we
            do, we&apos;ll tell the people affected.
          </p>
          <p>
            We may also update these terms. We&apos;ll change the date at the top and tell people on
            the list about important changes before they apply. You can leave at any time if you
            don&apos;t agree.
          </p>
        </div>

        <div className="space-y-4">
          <H>17. Law and disputes</H>
          <p>
            These terms are governed by Indian law. The courts in Bengaluru, Karnataka will handle
            any dispute. This doesn&apos;t affect your right to approach a consumer commission.
          </p>
        </div>

        <div className="space-y-4">
          <H>18. Contact and complaints</H>
          <p>For questions or complaints:</p>
          <p>
            Aarya, Founder and Grievance Officer
            <br />
            Email: <A>nostosmylkco@gmail.com</A>
            <br />
            Address: Nostos, Sarjapur, Bengaluru, Karnataka, India
          </p>
          <p>We&apos;ll respond within 30 days.</p>
        </div>

        <div className="space-y-4">
          <H>19. General</H>
          <p>
            These terms and our Privacy Policy are the full agreement between you and Nostos about
            the website, the waitlist and the Founding Community. If any part of these terms
            can&apos;t be enforced, the rest still applies.
          </p>
        </div>
      </section>
    </LegalLayout>
  );
}
